import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { LoadingBlock, ErrorState, Button, toast } from "@/components/ds";
import { PageTitle, Badge, Card, money } from "./_ui";

export default function Payouts() {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [pay, setPay] = useState({ payment_date: "", method: "", reference: "", notes: "" });
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["payouts"], queryFn: () => api.salesPayouts() });

  const create = async () => {
    try { await api.salesCreatePayout({}); toast.success("Draft payout batch created."); qc.invalidateQueries({ queryKey: ["payouts"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setStatus = async (id: string, status: string) => {
    try { await api.salesSetPayoutStatus(id, status); toast.success(`Batch ${status}.`); qc.invalidateQueries({ queryKey: ["payouts"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const markPaid = async (id: string) => {
    if (!pay.payment_date || !pay.method) { toast.error("Payment date and method are required (paid externally)."); return; }
    try { await api.salesMarkPayoutPaid(id, pay); toast.success("Recorded as paid externally."); setOpen(null); setPay({ payment_date: "", method: "", reference: "", notes: "" }); qc.invalidateQueries({ queryKey: ["payouts"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const payViaStripe = async (id: string, userId: string) => {
    try { const r = await api.salesPayRep(id, userId); toast.success(`Stripe transfer ${r.status} (${r.transfer_id}).`); qc.invalidateQueries({ queryKey: ["payouts"] }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (isLoading) return <LoadingBlock rows={6} />;
  if (isError || !data) return <ErrorState onRetry={refetch} />;

  return (
    <div data-testid="sales-payouts">
      <PageTitle title="Payout Batches" subtitle="No money is sent automatically — mark as Paid Externally once you've paid a rep."
        right={<Button onClick={create} data-testid="create-payout"><Plus className="mr-1 h-4 w-4" /> New draft batch</Button>} />

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800" data-testid="payout-honest-note">
        Payouts are recorded here for accounting only. ResFil does not fake or send Stripe payouts to sales reps.
      </div>

      <div className="mt-4 space-y-4">
        {data.batches.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No payout batches yet.</p>}
        {data.batches.map((b: any) => (
          <Card key={b.id} title={`${b.label} · ${money(b.total)}`} right={
            <div className="flex items-center gap-2">
              <Badge status={b.status} />
              <a href={api.salesPayoutExportUrl(b.id)} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50" data-testid={`export-${b.id}`}><Download className="h-3 w-3" /> CSV</a>
            </div>
          }>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400"><th className="py-1">Rep</th><th>Base</th><th>Gen</th><th>Growth</th><th>Pro</th><th>Custom</th><th>Adj</th><th>Total Due</th></tr></thead>
              <tbody>
                {b.lines.map((l: any) => (
                  <tr key={l.user_id} className="border-t border-slate-50" data-testid={`payout-line-${l.user_id}`}>
                    <td className="py-1">{l.rep_name}</td><td>{money(l.base_commission)}</td>
                    <td>{money(l.general_bonus)}</td><td>{money(l.growth_accelerator)}</td>
                    <td>{money(l.pro_accelerator)}</td><td>{money(l.custom_accelerator)}</td>
                    <td>{money(l.adjustments)}</td><td className="font-semibold">{money(l.total_due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {b.status === "draft" && <Button size="sm" variant="outline" onClick={() => setStatus(b.id, "approved")} data-testid={`approve-${b.id}`}>Approve</Button>}
              {b.status === "approved" && <Button size="sm" variant="outline" onClick={() => setStatus(b.id, "processing")} data-testid={`process-${b.id}`}>Mark processing</Button>}
              {(b.status === "approved" || b.status === "processing") && <Button size="sm" onClick={() => setOpen(open === b.id ? null : b.id)} data-testid={`markpaid-open-${b.id}`}>Mark Paid Externally</Button>}
              {(b.status === "approved" || b.status === "processing") && b.lines.map((l: any) => (
                <Button key={l.user_id} size="sm" variant="outline" onClick={() => payViaStripe(b.id, l.user_id)} data-testid={`pay-stripe-${b.id}-${l.user_id}`}>Pay {l.rep_name} via Stripe</Button>
              ))}
              {b.status === "paid" && b.payment && <span className="text-xs text-slate-500">Paid {b.payment.payment_date} via {b.payment.method}{b.payment.reference ? ` · ${b.payment.reference}` : ""}</span>}
            </div>
            {open === b.id && (
              <div className="mt-3 grid gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2" data-testid={`markpaid-form-${b.id}`}>
                <input type="date" value={pay.payment_date} onChange={(e) => setPay({ ...pay, payment_date: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1 text-sm" data-testid="pay-date" />
                <input placeholder="Method (e.g. ACH, Wire)" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1 text-sm" data-testid="pay-method" />
                <input placeholder="Reference #" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1 text-sm" data-testid="pay-ref" />
                <input placeholder="Notes" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} className="rounded-md border border-slate-200 px-2 py-1 text-sm" data-testid="pay-notes" />
                <Button size="sm" onClick={() => markPaid(b.id)} className="sm:col-span-2" data-testid="pay-submit">Record payment</Button>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
